import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { HardHat, Plus, Trash2, Loader2, StickyNote, Camera, ChevronDown, ChevronUp, Pencil, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const SubItemsPanel = ({ projectId, sub, isAdmin, canUpload }) => {
  const [items, setItems] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoDesc, setPhotoDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const fetchItems = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/subcontractors/${sub.sub_id}/items`);
      setItems(r.data);
    } catch (e) { /* noop */ }
  }, [projectId, sub.sub_id]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/projects/${projectId}/subcontractors/${sub.sub_id}/notes`, { text: noteText.trim() });
      setNoteText('');
      toast.success('Note added');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    } finally { setBusy(false); }
  };

  const uploadPhoto = async () => {
    if (photoFiles.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      photoFiles.forEach((f) => fd.append('files', f));
      if (photoDesc.trim()) fd.append('description', photoDesc.trim());
      await axios.post(`${API}/projects/${projectId}/subcontractors/${sub.sub_id}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPhotoFiles([]);
      setPhotoDesc('');
      toast.success(`Photo${photoFiles.length !== 1 ? 's' : ''} uploaded — ${sub.sub_name} notified`);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload photo');
    } finally { setBusy(false); }
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/subcontractors/${sub.sub_id}/items/${itemId}`);
      toast.success('Deleted');
      fetchItems();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const saveEdit = async (itemId) => {
    try {
      await axios.put(`${API}/projects/${projectId}/subcontractors/${sub.sub_id}/items/${itemId}`, { text: editText });
      toast.success('Updated');
      setEditingId(null);
      fetchItems();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  return (
    <div className="mt-3 space-y-3">
      {canUpload && (
        <div className="space-y-3 p-3 rounded-lg bg-gray-100 border border-gray-200">
          {isAdmin && (
          <div className="flex gap-2">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={`Write a note for ${sub.sub_name}...`}
              rows={2}
              className="flex-1 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 text-sm"
              data-testid={`sub-note-input-${sub.sub_id}`}
            />
            <Button
              onClick={addNote}
              disabled={busy || !noteText.trim()}
              className="bg-amber-600 hover:bg-amber-700 self-end"
              data-testid={`add-sub-note-btn-${sub.sub_id}`}
            >
              <StickyNote className="w-4 h-4" />
            </Button>
          </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setPhotoFiles(Array.from(e.target.files || []))}
              className="flex-1 bg-white border-2 border-slate-300 text-slate-900 text-sm"
              data-testid={`sub-photo-input-${sub.sub_id}`}
            />
            <Input
              value={photoDesc}
              onChange={(e) => setPhotoDesc(e.target.value)}
              placeholder="Photo description (optional)"
              className="flex-1 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 text-sm"
            />
            <Button
              onClick={uploadPhoto}
              disabled={busy || photoFiles.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid={`upload-sub-photo-btn-${sub.sub_id}`}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-gray-500 text-xs text-center py-2">No photos or notes for {sub.sub_name} yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200" data-testid={`sub-item-${item.id}`}>
              {item.type === 'photo' ? (
                <img
                  src={`${BACKEND_URL}${item.file_path}`}
                  alt={item.text || 'Photo'}
                  className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                />
              ) : (
                <StickyNote className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              )}
              <div className="min-w-0 flex-1">
                {editingId === item.id ? (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      placeholder="Description..."
                      className="flex-1 bg-white border-2 border-slate-300 text-slate-900 text-sm"
                      data-testid={`edit-sub-item-input-${item.id}`}
                    />
                    <div className="flex gap-1">
                      <Button size="sm" onClick={() => saveEdit(item.id)} className="bg-green-600 hover:bg-green-700 text-white h-8" data-testid={`save-sub-item-${item.id}`}>
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="border-gray-400 text-gray-700 h-8">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    {item.text && <p className="text-gray-900 text-sm whitespace-pre-wrap">{item.text}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      {item.uploaded_by_name} · {new Date(item.created_at).toLocaleDateString()}
                    </p>
                  </>
                )}
              </div>
              {isAdmin && editingId !== item.id && (
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setEditingId(item.id); setEditText(item.text || ''); }}
                    className="text-blue-500 hover:text-blue-600 hover:bg-blue-500/10 h-8 w-8 p-0"
                    data-testid={`edit-sub-item-${item.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteItem(item.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                    data-testid={`delete-sub-item-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ProjectSubContractors = ({ projectId, user }) => {
  const [subs, setSubs] = useState([]);
  const [allSubs, setAllSubs] = useState([]);
  const [selectedSubId, setSelectedSubId] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [adding, setAdding] = useState(false);

  const isAdmin = user.role === 'admin' || user.role === 'supervisor';
  const canUpload = isAdmin || user.role === 'employee';

  const fetchSubs = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/subcontractors`);
      setSubs(r.data);
    } catch (e) { /* noop */ }
  }, [projectId]);

  useEffect(() => {
    fetchSubs();
    if (isAdmin) {
      axios.get(`${API}/users`)
        .then((r) => setAllSubs(r.data.filter((u) => u.role === 'sub_contractor')))
        .catch(() => {});
    }
  }, [fetchSubs, isAdmin]);

  const handleAdd = async () => {
    if (!selectedSubId) return;
    setAdding(true);
    try {
      await axios.post(`${API}/projects/${projectId}/subcontractors`, { sub_id: selectedSubId });
      setSelectedSubId('');
      toast.success('Sub-contractor added');
      fetchSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add sub-contractor');
    } finally { setAdding(false); }
  };

  const handleRemove = async (entryId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/subcontractors/${entryId}`);
      toast.success('Sub-contractor removed');
      fetchSubs();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const availableSubs = allSubs.filter((s) => !subs.some((x) => x.sub_id === s.id));

  return (
    <div className="glass-card p-4 sm:p-6 mb-6" data-testid="project-subcontractors-card">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 flex items-center gap-2">
        <HardHat className="w-6 h-6 text-yellow-500" />
        Sub-Contractors
      </h2>

      {isAdmin && (
        <div className="flex gap-2 mb-4">
          <Select value={selectedSubId} onValueChange={setSelectedSubId}>
            <SelectTrigger className="flex-1 bg-white border-2 border-slate-300 text-slate-900" data-testid="sub-select-trigger">
              <SelectValue placeholder="Select a sub-contractor to add..." />
            </SelectTrigger>
            <SelectContent>
              {availableSubs.length === 0 ? (
                <div className="px-3 py-2 text-sm text-slate-500">No sub-contractors available</div>
              ) : (
                availableSubs.map((s) => (
                  <SelectItem key={s.id} value={s.id} data-testid={`sub-option-${s.id}`}>
                    {s.name} ({s.email})
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <Button
            onClick={handleAdd}
            disabled={adding || !selectedSubId}
            className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-black font-semibold min-h-[44px]"
            data-testid="add-subcontractor-btn"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      )}

      {subs.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-3" data-testid="no-subcontractors-message">
          No sub-contractors added to this project yet.
        </p>
      ) : (
        <div className="space-y-3">
          {subs.map((sub) => (
            <div key={sub.id} className="rounded-lg border border-gray-200 bg-gray-50 p-3" data-testid={`subcontractor-row-${sub.sub_id}`}>
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
                  className="flex items-center gap-2 text-left flex-1 min-w-0"
                  data-testid={`toggle-sub-panel-${sub.sub_id}`}
                >
                  <HardHat className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                  <span className="font-medium text-sm truncate text-gray-900">{sub.sub_name}</span>
                  {expandedId === sub.id ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                </button>
                {isAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemove(sub.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                    data-testid={`remove-subcontractor-${sub.sub_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
              {expandedId === sub.id && <SubItemsPanel projectId={projectId} sub={sub} isAdmin={isAdmin} canUpload={canUpload} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProjectSubContractors;
